import * as React from 'react';
import { observer } from 'mobx-react';
import { Modal, ModalChildStyle } from './modal';
import { style } from 'typestyle';
import { colorBrand, colorContrast } from '../theme/color';
import { percent, px } from 'csx';
import { TextNoOrphan } from './text-no-orphan';
import { ButtonPill } from './button-pill';
import { endJustified, horizontal, horizontallySpaced, vertical, verticallySpaced } from 'csstips';
import { FormInputCheckbox } from './form-input-checkbox';
import {CopyBox} from './copy-box';
import {Alert} from './alert';
import {AnalyticsController} from "../modules/analytics/controller";

export interface DownloadModalModel {
  show: boolean;
  agree: boolean;
  loading: boolean;
  error: string;
  downloadURL: string;
  attribution?: string;
  downloadLimitReached: boolean;
  licenseIdentity: string;
}

export interface DownloadModalProps {
  onRequestClose(): void;
  onAgreeToTerms(agree: boolean): void;
  onDownloadOriginal?(identity: string): Promise<any>;
  onDownloadInstrumental?(): void;
  onDownloadVocal?(): void;
  onSendAllToDropbox?(): void;
  model: DownloadModalModel;
  analytics: AnalyticsController;
  i18n?: {
    title: string;
    strap: string;
    agree: string;
    preparing: string;
    download: string;
    original: string;
    instrumental: string;
    vocal: string;
    dropbox: string;
    close: string;
  }
}

export interface DownloadModalState {
  accept: boolean;
}

@observer
export class DownloadModal extends React.Component<DownloadModalProps, DownloadModalState> {
  constructor(props) {
    super(props);
    this.state = { accept: false };

    this.acknowledgeTrackDownload = this.acknowledgeTrackDownload.bind(this);
  }

  acknowledgeTrackDownload() {
    const { model } = this.props;

    if (!model.agree || model.downloadLimitReached) {
      return null;
    }

    let analytics = this.props.analytics;
    this.props.onDownloadOriginal(model.licenseIdentity).then(function() {
        analytics.sendMixpanel("Downloading track", {
            "license_identity": model.licenseIdentity
        });

        window.location.href = model.downloadURL;
    });
  }

  render() {
    const {
      onRequestClose,
      onAgreeToTerms,
      model,
      i18n = DownloadModal.i18n
    } = this.props;

    return (
      <Modal show={model.show} onRequestClose={onRequestClose}>
        {model.show && (
          <div className={DownloadModal.styles.container}>
            <h3>
              <TextNoOrphan value={i18n.title} />
            </h3>
            <p>{i18n.strap}</p>
            {model.attribution && !model.error && (
              <CopyBox
                className={DownloadModal.styles.copyBox}
                content={model.attribution}
              />
            )}
            {!model.error && !model.downloadLimitReached && (
              <div>
                <FormInputCheckbox
                  name="agree"
                  id="agree-to-download"
                  value={model.agree}
                  placeholder={i18n.agree}
                  onChange={(_, agree) => onAgreeToTerms(agree)}
                />
              </div>
            )}
            {model.error && (
              <Alert type="warning" position={'none'}>{model.error}</Alert>
            )}
            <ul>
              <li>
              {!model.error && model.loading && (
                <ButtonPill muted={true} disabled={true} cursor="default" darken={true}>{i18n.preparing}</ButtonPill>
              )}
              {!model.loading && !model.error && !model.downloadLimitReached && model.agree && (
                <ButtonPill onClick={this.acknowledgeTrackDownload} primary={true} disabled={false}>
                  {i18n.download}
                </ButtonPill>
              )}
              {!model.loading && !model.error && !model.downloadLimitReached && !model.agree && (
                <ButtonPill disabled={true} muted={true} cursor="default" darken={true}>
                  {i18n.download}
                </ButtonPill>
              )}
              {model.downloadLimitReached && (
                <div className={DownloadModal.styles.downloadLimitReachedDiv}>
                  <p>We love that you're loving this tune, but downloads are limited to 3.</p>
                </div>
              )}
              </li>
            </ul>
            <div className={DownloadModal.styles.buttons}>
              <ButtonPill onClick={onRequestClose}>
                {i18n.close}
              </ButtonPill>
            </div>
          </div>
        )}
      </Modal>
    )
  }

  static i18n = {
    title: 'Download audio',
    strap: 'We provide a 320kbs mp3 file for use in your video',
    agree: 'I understand I should add the Lickd licensing information to my video description',
    // strap: 'Select which version of the track to download, or send all versions to your Dropbox',
    preparing: 'Preparing download',
    download: 'Download track',
    original: 'Download original',
    instrumental: 'Download instrumental',
    vocal: 'Download vocal',
    dropbox: 'Send all to Dropbox',
    close: 'Close'
  };

  static styles = {
    container: style({
      ...ModalChildStyle,
      position: 'relative',
      width: px(640),
      $nest: {
        '&> h3': {
          color: colorBrand.toString(),
          fontSize: px(30),
          margin: 0,
        },
        '&> p': {
          margin: '30px 0 0 0',
        },
        '&> ul': {
          ...vertical,
          ...verticallySpaced(10),
          listStyle: 'none inside none',
          margin: '10px 0 0 0',
          padding: 0
        },
        '&> ul li': {
          width: percent(100)
        },
        '&> ul [role="button"]': {
          width: percent(100)
        },
        '&> ul a': {
          width: percent(100)
        }
      }
    }),
    buttons: style({
      ...horizontal,
      ...horizontallySpaced(10),
      ...endJustified,
      margin: '30px 0 0 0'
    }),
    copyBox: style({
      margin: '10px 0'
    }),
    downloadLimitReachedDiv: style({
      textAlign: 'center',
      $nest: {
        '&> p': {
          marginBottom: '0px',
          color: colorContrast.toString()
        }
      }
    })
  }
}